const securityContent = {
  title: "Security & Data Integrity in Sortio",
  description: "Deep dive into Sortio's security features and comprehensive data integrity protections.",
  sections: [
    {
      id: "overview",
      title: "Overview",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">At Sortio, we understand that your files represent valuable data, memories, and work. That's why we've implemented comprehensive security and data integrity measures to ensure your files remain safe, private, and protected during every operation.</p>
      `
    },
    {
      id: "enterprise-security",
      title: "Enterprise-Grade Security",
      content: `
        <div class="space-y-8 mt-6">
          <div class="border border-[#e0e0e5] rounded-xl p-6 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-4 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Encryption in Transit and at Rest
            </h4>
            <p class="text-[#424245] mb-4">Your data is encrypted in transit using TLS 1.2 or higher and at rest on our managed infrastructure. In cloud mode, your filenames and metadata are processed by Sortio and your chosen AI provider to plan the sort, so they are readable during processing. To keep everything on your machine, use Offline mode (Ollama) or bring your own API key.</p>
          </div>
          
          <div class="border border-[#e0e0e5] rounded-xl p-6 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-4 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Secure Authentication
            </h4>
            <p class="text-[#424245] mb-4">We use Auth0, a leader in identity management, to ensure secure login and protect your account from unauthorized access. This robust authentication system provides an additional layer of security for your Sortio account.</p>
          </div>
          
          <div class="border border-[#e0e0e5] rounded-xl p-6 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-4 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Privacy-Preserving AI
            </h4>
            <p class="text-[#424245] mb-4">In cloud mode (the default), Sortio sends your filenames and metadata to plan the sort, so they are readable during processing; file contents are sent only if you enable a content-based feature. Data is encrypted in transit (TLS 1.2+) and at rest, but not end-to-end, and Sortio does not retain your file contents. To keep everything on your machine, use Offline mode (Ollama) or bring your own API key.</p>
          </div>
        </div>
      `
    },
    {
      id: "data-integrity",
      title: "How Sortio Protects Your Files",
      content: `
        <div class="space-y-8 mt-6">
          <div class="border border-[#e0e0e5] rounded-xl p-6 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-4">Sortio moves files. It never deletes them.</h4>
            <p class="text-[#424245] mb-4">
              Applying a sort moves each file into a new subfolder of the folder you selected.
              Nothing is copied, nothing is sent anywhere, and nothing leaves the folder you
              picked. There is no code path in Sortio that deletes one of your files as part of
              a sort.
            </p>
            <p class="text-[#424245]">
              A file is never overwritten. If a file with the same name already exists at the
              destination, Sortio gives the incoming file a unique name instead of replacing
              what is there.
            </p>
          </div>

          <div class="border border-[#e0e0e5] rounded-xl p-6 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-4">A safety copy before every sort</h4>
            <p class="text-[#424245] mb-4">
              Before Sortio moves anything, it writes a zip of the files it is about to touch to
              <code>~/.sortio/backups</code>, along with a manifest recording each file's original
              path, size, and checksum.
            </p>
            <ul class="space-y-3">
              <li class="flex items-start">
                <span class="text-[#424245]">
                  <strong>Sortio keeps the backups from your 10 most recent sorts.</strong> This is a
                  count, not a time limit. An eleventh sort removes the oldest zip, whether that
                  sort happened last month or ten minutes ago.
                </span>
              </li>
              <li class="flex items-start">
                <span class="text-[#424245]">
                  <strong>Backups have a size limit, 500 MB by default.</strong> If the files in a
                  sort add up to more than that, Sortio skips the backup for that sort and the
                  sort proceeds. You can raise or remove the limit in Settings, where 0 means no
                  limit.
                </span>
              </li>
              <li class="flex items-start">
                <span class="text-[#424245]">
                  <strong>Undo does not depend on the backup.</strong> Undo replays the recorded
                  moves in reverse, so it works even for a sort whose backup was skipped.
                </span>
              </li>
            </ul>
          </div>

          <div class="border border-[#e0e0e5] rounded-xl p-6 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-4">Every sort is recorded and reversible</h4>
            <p class="text-[#424245] mb-4">
              Sortio writes every file move to a local history database, including a fingerprint
              of each file, and keeps that history indefinitely. You can undo any sort from the
              History screen, including sorts from previous sessions.
            </p>
            <p class="text-[#424245]">
              If some files cannot be put back, because you moved or opened them elsewhere in the
              meantime, Sortio restores the ones it can, tells you exactly how many it could not,
              and leaves the sort undoable so you can finish later.
            </p>
          </div>
        </div>
      `
    },
    {
      id: "why-it-matters",
      title: "Why This Matters",
      content: `
        <div class="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-6">
          <p class="text-[#424245] mb-4">These security and data integrity measures mean that when you use Sortio to organize your files:</p>
          <ol class="space-y-3 list-decimal pl-5 text-[#424245]">
            <li><strong>Your data is encrypted in transit (TLS 1.2+) and at rest</strong>, and stays on your machine in Offline mode or with BYOK</li>
            <li><strong>Your original files remain safe and recoverable</strong></li>
            <li><strong>File operations complete reliably</strong> or safely roll back</li>
            <li><strong>The risk of data corruption or loss is minimized</strong></li>
            <li><strong>You can undo operations with confidence</strong></li>
          </ol>
          
          <p class="text-[#424245] mt-4">
            For developers and technical users interested in implementation details, our 
            <a href="https://github.com/sortio/sortio/tree/main/src-tauri/tests" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">
              integration tests
            </a> 
            demonstrate how these features work together to ensure data integrity.
          </p>
        </div>
      `
    }
  ]
};

export default securityContent;